import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { search } from '../../../../actions/searchActions';
import { getCurrentCulture, getQueryParam } from '../../../../helpers/url/urlHelpers';
import useOnClickOutside from '../../../../hooks/useOnOutsideClick';
import { ReactComponent as Search } from '../../../../icons/search-2.svg';
import ITAutocompleteReducer from '../../../../interfaces/autocomplete/autocompleteReducer';
import { IStore } from '../../../../reducers';
import IconButton from '../../../iconButton';
import InputWithAutocomplete from '../../inputWithAutocomplete';
import { Form, SearchInputWrapper, SubmitButtonWrapper, Wrapper } from './searchForm.styles';

interface Props {
	history: any;
	inputPlaceholder: string;
	className?: string;
}

const SearchForm: FC<Props> = ({ history, inputPlaceholder, className }) => {
	const lang = getCurrentCulture();
	const dispatch = useDispatch<any>();
	const { t } = useTranslation('search');
	const query = getQueryParam('q');

	const [inputValue, setInputValue] = useState(query || '');
	const [autocompleteIsOpen, openAutocomplete] = useState(false);

	const autocompleteReducer: ITAutocompleteReducer = useSelector<IStore, ITAutocompleteReducer>(
		store => store.autocompleteReducer
	);
	const { icelandicPages, englishPages } = autocompleteReducer;

	const searchInputWrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setInputValue(query || '');
		dispatch(search(query || '', lang));
	}, [query, dispatch, lang]);

	const onChange = (value: string) => {
		if (!autocompleteIsOpen) {
			openAutocomplete(true);
		}
		setInputValue(value);
	};

	const doSubmit = () => {
		openAutocomplete(false);
		const basePath = lang === 'is' ? '/leit' : '/en/search';
		history.push(`${basePath}${inputValue ? `?q=${inputValue}` : ''}`);
	};

	const onAutocompleteSelection = (value: string) => {
		if (value) {
			setInputValue(value);
			doSubmit();
		}
	};

	const closeAutocomplete = () => {
		openAutocomplete(false);
	};

	const handleSearchSubmit = (event: Event) => {
		event.preventDefault();
		doSubmit();
	};

	useOnClickOutside(
		searchInputWrapperRef,
		() => {
			openAutocomplete(false);
		},
		autocompleteIsOpen,
		'mouseup'
	);

	return (
		<Wrapper className={className}>
			<Form onSubmit={handleSearchSubmit}>
				<SearchInputWrapper ref={searchInputWrapperRef}>
					<InputWithAutocomplete
						value={inputValue}
						placeholder={inputPlaceholder}
						onChange={onChange}
						onFocusLose={closeAutocomplete}
						onDropdownSelection={onAutocompleteSelection}
						options={lang === 'is' ? icelandicPages : englishPages}
						autocompleteIsOpen={autocompleteIsOpen}
					/>
					<SubmitButtonWrapper>
						<IconButton onClick={doSubmit} onFocus={closeAutocomplete} title={t('search')}>
							<Search />
						</IconButton>
					</SubmitButtonWrapper>
				</SearchInputWrapper>
			</Form>
		</Wrapper>
	);
};

export default withRouter(SearchForm);
